import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  IconButton,
  Box,
  Button,
  Link,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";

import logo from "../../../assets/images/logo.png";

// Constantes de diseño
const SECONDARY_BLUE = "#4669E8";
const HOVER_BLUE = "#2952CC";
const HEADER_HEIGHT_MOBILE = "60px";
const HEADER_HEIGHT_DESKTOP = "70px";
const HEADER_BORDER_RADIUS = "16px";
const EXPAND_DURATION = "350ms";

const LoginButton = styled(Button)(() => ({
  backgroundColor: SECONDARY_BLUE,
  color: "white",
  borderRadius: "20px",
  textTransform: "none",
  fontFamily: "Quicksand-Regular",
  fontSize: "15px",
  fontWeight: "normal",
  padding: "8px 24px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en hover
    boxShadow: "none", // Agregado: sin sombra en hover
  },
  "&:focus": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en focus
    boxShadow: "none", // Agregado: sin sombra en focus
  },
  "&:focus-visible": {
    backgroundColor: SECONDARY_BLUE, // Agregado: mantener color original en focus visible
    boxShadow: "none", // Agregado: sin sombra en focus visible
    outline: "none", // Agregado: sin outline en focus
  },
  "&:active": {
    backgroundColor: HOVER_BLUE,
    boxShadow: "none",
  },
}));

const RegisterLink = styled(Typography)(() => ({
  fontFamily: "Quicksand-Regular",
  fontSize: "16px",
  cursor: "pointer",
  color: SECONDARY_BLUE,
  textAlign: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    color: HOVER_BLUE,
    textDecoration: "underline",
  },
}));

export default function HeaderClientes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Resetear menú móvil cuando cambia el breakpoint
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  // Cerrar menú móvil al hacer scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (mobileMenuOpen && window.scrollY > 50) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileMenuOpen, isMobile]);

  // Prevenir scroll del body cuando el menú está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setMobileMenuOpen((prev) => !prev);
    setTimeout(() => setIsAnimating(false), 350);
  }, [isAnimating]);

// Desktop Header
  if (!isMobile) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: "1200px", // Corregido: Container lg de MUI = 1200px
          width: "calc(100% - 48px)",
          zIndex: 1100,
          background: "rgba(255, 255, 255, 0.4)", // Cambiado: más transparente
          backdropFilter: "blur(20px)", // Agregado: efecto blur
          WebkitBackdropFilter: "blur(20px)", // Agregado: soporte Safari
          borderRadius: HEADER_BORDER_RADIUS,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          // Removido: transition y &:hover para eliminar efecto de sombra
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: HEADER_HEIGHT_DESKTOP,
            px: 3, // 24px horizontal padding - Idéntico a HeaderProfesionales
          }}
        >
          {/* Logo */}
          <Box sx={{ flexShrink: 0 }}>
            <Link href="/" underline="none">
              <img
                src={logo}
                alt="Logo clinifica"
                style={{ height: "40px", display: "block" }}
              />
            </Link>
          </Box>

          {/* Botón Para Profesionales */}
          <Box sx={{ flexShrink: 0 }}>
            <Link
              href="/pro"
              underline="none"
              style={{ textDecoration: "none" }}
            >
              <LoginButton variant="contained" disableRipple disableElevation>Para Profesionales</LoginButton>
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  // Mobile Header
  return (
    <>
      {/* Backdrop */}
      {mobileMenuOpen && (
        <Box
          onClick={() => setMobileMenuOpen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1099,
            backgroundColor: "transparent",
          }}
        />
      )}

      {/* Header Móvil */}
      <Box
        sx={{
          position: "fixed",
          top: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: "1152px", // Ancho efectivo del contenido en Container lg - Idéntico a HeaderProfesionales
          width: "calc(100% - 32px)", // 16px padding cada lado - Idéntico a HeaderProfesionales
          zIndex: 1100,
          background: "rgba(255, 255, 255, 0.4)", // Cambiado: más transparente
          backdropFilter: "blur(20px)", // Agregado: efecto blur
          WebkitBackdropFilter: "blur(20px)", // Agregado: soporte Safari
          borderRadius: HEADER_BORDER_RADIUS,
          boxShadow: mobileMenuOpen
            ? "0 8px 32px rgba(0, 0, 0, 0.15)"
            : "0 2px 8px rgba(0, 0, 0, 0.08)",
          transition: `all ${EXPAND_DURATION} ease-out`,
          maxHeight: mobileMenuOpen ? "200px" : HEADER_HEIGHT_MOBILE,
          overflow: "hidden",
        }}
      >
        {/* Header cerrado */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: HEADER_HEIGHT_MOBILE,
            px: 2, // 16px horizontal padding - Idéntico a HeaderProfesionales
          }}
        >
          <Link href="/" underline="none">
            <img
              src={logo}
              alt="Logo clinifica"
              style={{ height: "30px", display: "block" }}
            />
          </Link>

          <IconButton
            onClick={toggleMobileMenu}
            disabled={isAnimating}
            sx={{ color: "#aeaeae" }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        {/* Menú expandido */}
        {mobileMenuOpen && (
          <Box
            sx={{
              px: 3,
              pb: 4,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              opacity: mobileMenuOpen ? 1 : 0,
              transition: `opacity 300ms ease-out ${mobileMenuOpen ? "150ms" : "0ms"}`,
            }}
          >
            {/* Botón Para Profesionales */}
            <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
              <Link
                href="/pro"
                underline="none"
                style={{ textDecoration: "none" }}
              >
                <LoginButton
                  variant="contained"
                  disableRipple
                  disableElevation
                  sx={{ width: "100%", py: 0.5 }}
                >
                  Para Profesionales
                </LoginButton>
              </Link>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
